import { motion } from "framer-motion";
import { Gift, Cake, Heart, Sparkles, Store, ExternalLink, Phone, MapPin, Clock } from "lucide-react";
import { BRAND } from "@/lib/data";
import { useContent } from "@/lib/content";
import { Overline } from "@/components/Overline";
import { Seo } from "@/components/Seo";

const ease = [0.16, 1, 0.3, 1];
const GUTSCHEIN_URL = "https://www.bon-bon.de/gutschein/la-contadina/";

const OCCASIONS = [
  { icon: Cake, label: "Geburtstag", text: "Feiern bei einem Festessen unter Freunden." },
  { icon: Heart, label: "Jubiläum", text: "Ein romantischer Abend zu zweit." },
  { icon: Gift, label: "Weihnachten", text: "Eine geschmackvolle Geschenkidee zum Fest." },
  { icon: Sparkles, label: "Einfach so", text: "Ein Dankeschön oder eine kleine Aufmerksamkeit." },
];

const AMOUNTS = ["25 €", "50 €", "75 €", "100 €", "Wunschbetrag"];

export default function Gutschein() {
  const c = useContent();
  const hero = c.images.gutscheinHero;
  const t = c.text.gutschein;

  return (
    <div data-testid="gutschein-page" className="bg-cream">
      <Seo page="gutschein" />

      {/* HERO */}
      <section className={`pt-44 pb-16 relative ${hero ? "" : "bg-sand grain"}`}>
        {hero && (
          <>
            <div className="absolute inset-0"><img src={hero} alt="" className="w-full h-full object-cover" data-testid="gutschein-hero-img" /></div>
            <div className="absolute inset-0 bg-ink/60" />
          </>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Overline light={!!hero} className="!flex justify-center">Verschenke Genuss</Overline>
          <h1 className={`font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight ${hero ? "text-cream" : "text-ink"}`}>{t.heroTitle}</h1>
          <p className={`font-light mt-5 max-w-2xl mx-auto ${hero ? "text-cream/85" : "text-ink-soft"}`}>
            {t.heroText}
          </p>
        </div>
      </section>

      {/* OCCASIONS */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {OCCASIONS.map((o, i) => (
              <motion.div
                key={o.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1, ease }}
                className="bg-sand border border-gold/20 p-8 text-center group"
                data-testid={`gutschein-occasion-${i}`}
              >
                <o.icon className="mx-auto text-wine mb-4 transition-transform duration-500 group-hover:scale-110" size={30} strokeWidth={1.3} />
                <h3 className="font-serif text-2xl text-ink mb-2">{o.label}</h3>
                <p className="text-ink-soft font-light text-sm">{o.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TWO WAYS TO BUY */}
      <section className="py-16 md:py-24 bg-sand grain">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <Overline className="!flex justify-center">So erhalten Sie Ihren Gutschein</Overline>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-ink">Im Lokal oder online</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Im Lokal */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease }}
              className="bg-white border border-gold/20 p-8 flex flex-col"
              data-testid="gutschein-local"
            >
              <Store className="text-wine mb-4" size={32} strokeWidth={1.3} />
              <h3 className="font-serif text-2xl text-ink mb-3">Direkt bei uns im Restaurant</h3>
              <p className="text-ink-soft font-light text-sm leading-relaxed mb-6">
                Besuchen Sie uns in Dresden-Löbtau – wir stellen Ihren Gutschein in jedem Wunschbetrag persönlich aus.
                Ideal, wenn Sie das Geschenk gleich mitnehmen möchten.
              </p>
              <ul className="space-y-3 text-sm text-ink-soft mt-auto">
                <li className="flex gap-3"><MapPin size={18} className="text-gold shrink-0 mt-0.5" /><span>{BRAND.address}</span></li>
                <li><a href={BRAND.phoneHref} className="flex gap-3 hover:text-wine transition-colors"><Phone size={18} className="text-gold shrink-0" />{BRAND.phone}</a></li>
                <li className="flex gap-3"><Clock size={18} className="text-gold shrink-0 mt-0.5" /><span>Zu unseren Öffnungszeiten</span></li>
              </ul>
            </motion.div>

            {/* Online */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1, ease }}
              className="bg-wine text-cream p-8 flex flex-col"
              data-testid="gutschein-online"
            >
              <Gift className="text-gold mb-4" size={32} strokeWidth={1.3} />
              <h3 className="font-serif text-2xl mb-3">Bequem online bestellen</h3>
              <p className="text-cream/80 font-light text-sm leading-relaxed mb-6">
                Kaufen Sie Ihren Gutschein rund um die Uhr über unser Partner-Portal <strong>Bon-Bon</strong>.
                Sofort per E-Mail erhältlich – perfekt für spontane Geschenke.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {AMOUNTS.map((a) => (
                  <span key={a} className="border border-gold/40 text-gold text-xs tracking-[0.15em] uppercase px-3 py-1.5 rounded-full">{a}</span>
                ))}
              </div>
              <a
                href={GUTSCHEIN_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="gutschein-online-cta"
                className="btn-pop bg-gold text-ink hover:bg-cream px-8 py-4 uppercase text-xs tracking-[0.25em] inline-flex items-center justify-center gap-2 mt-auto"
              >
                Online kaufen <ExternalLink size={16} />
              </a>
            </motion.div>
          </div>

          <p className="text-center text-xs text-ink-muted mt-8">
            Der Online-Kauf erfolgt über unseren externen Partner Bon-Bon. Sie werden in einem neuen Fenster weitergeleitet.
          </p>
        </div>
      </section>
    </div>
  );
}
